import { useCallback, useMemo, useState } from "react";
import { get, useFormContext, useFormState } from "react-hook-form";
import { useIntl } from "react-intl";

import { useGetTimezones } from "api/queries/common/useGetTimezones";
import { StopTimeType, StopType } from "types/MultiStop";
import { useSchedule } from "@RequestFormV2/sections/Schedule/context";
import { getStopFieldPath } from "@RequestFormV2/sections/Schedule/utils/fieldPaths";
import { getTimeTypeOptionsForStop } from "@RequestFormV2/sections/Schedule/utils/scheduleOptions";

export function useStopPlanning(stopIndex: number) {
  const intl = useIntl();
  const { control, getValues, setValue, unregister, watch } = useFormContext();
  const { errors } = useFormState({ control });
  const { scheduleOptions } = useSchedule();

  const fieldPaths = useMemo(
    () => ({
      stop_time_type: getStopFieldPath(stopIndex, "stop_time_type"),
      stop_after_date: getStopFieldPath(stopIndex, "stop_after_date"),
      stop_after_time: getStopFieldPath(stopIndex, "stop_after_time"),
      stop_before_date: getStopFieldPath(stopIndex, "stop_before_date"),
      stop_before_time: getStopFieldPath(stopIndex, "stop_before_time"),
      stop_timezone: getStopFieldPath(stopIndex, "stop_timezone"),
      stop_hours_start: getStopFieldPath(stopIndex, "stop_hours_start"),
      stop_hours_end: getStopFieldPath(stopIndex, "stop_hours_end"),
      stop_notes: getStopFieldPath(stopIndex, "stop_notes"),
    }),
    [stopIndex],
  );

  const hoursFromKey = fieldPaths.stop_hours_start;
  const hoursToKey = fieldPaths.stop_hours_end;
  const notesKey = fieldPaths.stop_notes;

  const hasHours = getValues(hoursFromKey) || getValues(hoursToKey);
  const hasNotes = getValues(notesKey);

  const [showHour, setShowHour] = useState(Boolean(hasHours));
  const [showNotes, setShowNotes] = useState(Boolean(hasNotes));

  const stopType = watch(getStopFieldPath(stopIndex, "stop_type"));
  const isPickup = stopType === StopType.PICKUP;
  const resolvedStopType = isPickup ? StopType.PICKUP : StopType.DROPOFF;
  const timeType = watch(fieldPaths.stop_time_type);

  const isBefore = timeType === StopTimeType.BEFORE;
  const isBetween = timeType === StopTimeType.BETWEEN;
  const isAfter = timeType === StopTimeType.AFTER;
  const isAsap = timeType === StopTimeType.ASAP_DELIVERY;

  const { data: timezones = [] } = useGetTimezones({}, {
    select: (data: any) => data.map((tz: any) => ({ value: tz.value, label: tz.name })),
  });

  const timeTypeOptions = useMemo(
    () => getTimeTypeOptionsForStop(intl, resolvedStopType, scheduleOptions),
    [intl, resolvedStopType, scheduleOptions],
  );

  const timeTypeRadioOptions = useMemo(
    () =>
      timeTypeOptions.map((opt) => ({
        label: opt.label,
        value: String(opt.value),
      })),
    [timeTypeOptions],
  );

  const handleTimeTypeChange = useCallback(
    (nextTimeType: StopTimeType) => {
      setValue(fieldPaths.stop_time_type, nextTimeType);

      if (nextTimeType === StopTimeType.AFTER) {
        setValue(fieldPaths.stop_before_date, null);
        setValue(fieldPaths.stop_before_time, null);
      } else if (nextTimeType === StopTimeType.BEFORE) {
        setValue(fieldPaths.stop_after_date, null);
        setValue(fieldPaths.stop_after_time, null);
      } else if (nextTimeType === StopTimeType.ASAP_DELIVERY) {
        setValue(fieldPaths.stop_after_date, null);
        setValue(fieldPaths.stop_after_time, null);
        setValue(fieldPaths.stop_before_date, null);
        setValue(fieldPaths.stop_before_time, null);
      }
    },
    [setValue, fieldPaths],
  );

  const handleShowHour = useCallback(() => {
    if (showHour) {
      setValue(hoursFromKey, "");
      setValue(hoursToKey, "");
      unregister(hoursFromKey);
      unregister(hoursToKey);
    }
    setShowHour((prev) => !prev);
  }, [showHour, setValue, unregister, hoursFromKey, hoursToKey]);

  const handleShowNotes = useCallback(() => {
    if (showNotes) {
      setValue(notesKey, "");
      unregister(notesKey);
    }
    setShowNotes((prev) => !prev);
  }, [showNotes, setValue, unregister, notesKey]);

  const planningErrors = useMemo(
    () => ({
      stop_hours_start: get(errors, fieldPaths.stop_hours_start),
      stop_hours_end: get(errors, fieldPaths.stop_hours_end),
    }),
    [errors, fieldPaths.stop_hours_start, fieldPaths.stop_hours_end],
  );

  const handleAfterDateTimeChange = useCallback(() => {
    if (!isAfter) return;

    setValue(fieldPaths.stop_before_date, getValues(fieldPaths.stop_after_date));
    setValue(fieldPaths.stop_before_time, getValues(fieldPaths.stop_after_time));
  }, [getValues, isAfter, setValue, fieldPaths]);

  const handleBeforeDateTimeChange = useCallback(() => {
    if (!isBefore) return;

    setValue(fieldPaths.stop_after_date, getValues(fieldPaths.stop_before_date));
    setValue(fieldPaths.stop_after_time, getValues(fieldPaths.stop_before_time));
  }, [getValues, isBefore, setValue, fieldPaths]);

  return {
    fieldPaths,
    timeTypeOptions,
    timeTypeRadioOptions,
    handleTimeTypeChange,
    showHour,
    showNotes,
    handleShowHour,
    handleShowNotes,
    hoursFromKey,
    hoursToKey,
    notesKey,
    isPickup,
    isAsap,
    isBefore,
    isBetween,
    isAfter,
    handleAfterDateTimeChange,
    handleBeforeDateTimeChange,
    timezones,
    planningErrors,
  };
}

export type StopPlanningSlice = ReturnType<typeof useStopPlanning>;
